import { Injectable, inject } from '@angular/core';
import {
  Auth,
  User as FirebaseUser,
  GoogleAuthProvider,
  authState,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut
} from '@angular/fire/auth';
import { Firestore, doc, serverTimestamp, setDoc } from '@angular/fire/firestore';
import { Observable, from, map, shareReplay, switchMap } from 'rxjs';
import { User } from '../../interfaces/user';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly adminAllowList = new Set(
    environment.adminEmails.map((email) => email.trim().toLowerCase()).filter(Boolean)
  );

  readonly firebaseUser$: Observable<FirebaseUser | null> = authState(this.auth).pipe(shareReplay(1));

  readonly currentUser$: Observable<User | null> = this.firebaseUser$.pipe(
    map((firebaseUser) => (firebaseUser ? this.mapFirebaseUser(firebaseUser) : null)),
    shareReplay(1)
  );

  readonly isAuthenticated$: Observable<boolean> = this.firebaseUser$.pipe(map((firebaseUser) => !!firebaseUser));

  readonly isAdmin$: Observable<boolean> = this.currentUser$.pipe(map((user) => !!user && user.admin));

  logout(): Observable<void> {
    return from(signOut(this.auth));
  }

  /** Google sign-in (admin or viewer). Does not sign out non-admin users. */
  signInWithGoogle(): Observable<void> {
    const provider = new GoogleAuthProvider();
    return from(signInWithPopup(this.auth, provider)).pipe(switchMap((cred) => this.upsertUser(cred.user)));
  }

  /**
   * Sends a passwordless sign-in link. Store the email client-side (e.g. localStorage) before calling.
   * The continue URL must be listed under Firebase Auth authorized domains.
   */
  sendViewerEmailLink(email: string, continueUrl: string): Observable<void> {
    return from(
      sendSignInLinkToEmail(this.auth, email, {
        url: continueUrl,
        handleCodeInApp: true
      })
    );
  }

  /** Completes email-link sign-in after the user opens the link (same device). */
  completeViewerEmailLink(email: string, emailLinkUrl: string): Observable<void> {
    return from(signInWithEmailLink(this.auth, email, emailLinkUrl)).pipe(
      switchMap((cred) => this.upsertUser(cred.user))
    );
  }

  private upsertUser(firebaseUser: FirebaseUser): Observable<void> {
    const userRef = doc(this.firestore, `users/${firebaseUser.uid}`);
    const normalizedEmail = (firebaseUser.email ?? '').toLowerCase();
    const admin = this.adminAllowList.has(normalizedEmail);

    return from(
      setDoc(
        userRef,
        {
          id: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          name: firebaseUser.displayName ?? '',
          admin,
          authenticated: true,
          updated: serverTimestamp(),
          lastLogin: serverTimestamp(),
          created: serverTimestamp()
        },
        { merge: true }
      )
    );
  }

  private mapFirebaseUser(firebaseUser: FirebaseUser): User {
    const now = new Date();
    const normalizedEmail = (firebaseUser.email ?? '').toLowerCase();
    const admin = this.adminAllowList.has(normalizedEmail);

    return {
      id: firebaseUser.uid,
      email: firebaseUser.email ?? '',
      name: firebaseUser.displayName ?? undefined,
      authenticated: true,
      admin,
      created: now,
      updated: now,
      lastLogin: now
    };
  }
}
